// src/components/EventDetail.js - Updated to always regenerate heats when event is selected
import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useCompetition } from '../competition/CompetitionContext';
import auditLogger from '../utils/auditLogger';
import './EventDetail.css';
import HeatDistribution from './HeatDistribution';
import CompetitionControl from './CompetitionControl';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

// Helper function to generate section from event name (moved outside component to fix hoisting issue)
function generateSectionFromName(eventName) {
  // Try to extract age group, level, and style from event name
  // This is a fallback if section field is not available
  const AGE_GROUPS = ['Juvenile 1', 'Juvenile 2', 'Junior 1', 'Junior 2', 'Youth', 'Adult', 'Senior 1', 'Senior 2', 'Senior 3', 'Senior 4'];
  const LEVELS = ['Level 1', 'Level 2', 'Level 3', 'Level 4', 'Rising-Star', 'Championship', 'Professional'];
  const STYLES = ['Standard', 'Latin'];

  let ageGroup = 'Adult';
  let level = 'Championship';
  let style = 'Latin';

  // Try to find age group in name
  for (const ag of AGE_GROUPS) {
    if (eventName.includes(ag)) {
      ageGroup = ag;
      break;
    }
  }

  // Try to find level in name
  for (const l of LEVELS) {
    if (eventName.includes(l)) {
      level = l;
      break;
    }
  }

  // Try to find style in name
  for (const s of STYLES) {
    if (eventName.includes(s)) {
      style = s;
      break;
    }
  }

  return `${ageGroup} ${level} ${style}`;
}

const EventDetail = ({ event, onUpdateDisplay }) => {
  const { currentUser } = useAuth();
  const { selectedCompetition } = useCompetition();
  const [localEvent, setLocalEvent] = useState(event);
  const [activeTab, setActiveTab] = useState('dancers'); // 'dancers', 'heats', or 'control'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [heats, setHeats] = useState([]);
  const [isEditingName, setIsEditingName] = useState(false);
  const [updatedEventName, setUpdatedEventName] = useState(event.name);
  const [updatedSection, setUpdatedSection] = useState(event.section || generateSectionFromName(event.name));

  // Function to fetch fresh event data and merge with latest check-in status from couples
  const fetchFreshEventData = async () => {
    try {
      // Fetch both event and couples data
      const [eventResponse, couplesResponse] = await Promise.all([
        fetch(`https://${_host}:6003/api/${_company}/events/${event.id}`),
        fetch(`https://${_host}:6003/api/${_company}/couples`)
      ]);

      if (eventResponse.ok && couplesResponse.ok) {
        const freshEventData = await eventResponse.json();
        const couplesData = await couplesResponse.json();

        
        // Create a lookup map for couples by cloth number
        const couplesLookup = {};
        couplesData.forEach(couple => {
          couplesLookup[couple.clothNumber] = {
            registrationCheckin: couple.registrationCheckin || false,
            marshallCheckin: couple.marshallCheckin || false
          };
        });

        // Merge latest check-in status into event dancers
        const updatedDancers = freshEventData.dancers.map(dancer => {
          const coupleStatus = couplesLookup[dancer.clothNumber];
          if (coupleStatus) {
            return {
              ...dancer,
              registrationCheckin: coupleStatus.registrationCheckin,
              marshallCheckin: coupleStatus.marshallCheckin
            };
          }
          return dancer;
        });

        // Update event with merged dancer data
        const mergedEventData = {
          ...freshEventData,
          dancers: updatedDancers,
          // Convert snake_case to camelCase for navigation state
          currentDanceIndex: freshEventData.current_dance_index,
          currentHeatIndex: freshEventData.current_heat_index
        };

        
        setLocalEvent(mergedEventData);
              }
    } catch (error) {
      console.error('Error fetching fresh event data:', error);
    }
  };

  // Update local state when event prop changes and ALWAYS regenerate heats
  useEffect(() => {
    // Reset tab to dancers when event changes to prevent blank pages
    // This handles cases where previous event had Control Panel selected but new event isn't active
    setActiveTab('dancers');

    setLocalEvent(event);
    setUpdatedEventName(event.name);
    setUpdatedSection(event.section || generateSectionFromName(event.name));

    // Always regenerate heats when event changes, regardless of conditions
    // This ensures fresh heat distribution every time an event is selected
      // Note: Don't generate heats immediately here. Let fetchFreshEventData get the complete data first,
    // then generate heats in a separate effect after localEvent is properly set

  }, [event]); // Only depend on event changes

  // Set up polling to refresh event data for latest check-in status
  useEffect(() => {
    // Poll every 5 seconds to get latest check-in status
    const interval = setInterval(fetchFreshEventData, 5000);

    // Clean up interval on component unmount
    return () => clearInterval(interval);
  }, [event.id]); // Re-establish interval when event changes

  // Generate heats after localEvent is updated with complete data from API
  useEffect(() => {
    // Only generate heats if localEvent has the required fields (especially dances)
    if (localEvent && localEvent.dances && Array.isArray(localEvent.dances) && localEvent.dances.length > 0) {
        generateHeats(localEvent);
    } else if (localEvent) {
      // Still waiting for complete data
    }
  }, [localEvent]); // This effect runs when localEvent changes

  
  // Note: generateSectionFromName function moved outside component to fix hoisting issue

  // Function to add a dancer to the event
  const handleDancerAdded = async (dancer) => {
    try {
      // Update the local state with the new dancer
      const currentDancers = localEvent.dancers && Array.isArray(localEvent.dancers) ? localEvent.dancers : [];
      const updatedDancers = [...currentDancers, dancer];
      const updatedEvent = { ...localEvent, dancers: updatedDancers };
      
      setLocalEvent(updatedEvent);

      // Log dancer addition to event
      if (currentUser) {
        auditLogger.logAddDancerToEvent(
          currentUser.username || currentUser.id,
          dancer.name,
          event.id,
          event.name
        );
      }

      // Show success message
      setSuccess(`Dancer ${dancer.name} (Number ${dancer.clothNumber}) added successfully`);
      setTimeout(() => setSuccess(null), 3000);
      
      // Regenerate heats after adding dancer
      generateHeats(updatedEvent);
    } catch (err) {
      console.error('Error updating local state after dancer added:', err);
      setError(`Failed to update view: ${err.message}`);
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleUpdateEventName = async () => {
    try {
      setLoading(true);

      // Trim and validate the name
      const trimmedName = updatedEventName.trim();
      if (!trimmedName) {
        throw new Error('Event name cannot be empty');
      }

      // Generate section based on the updated name
      const generatedSection = generateSectionFromName(trimmedName);

      // Skip API call if name hasn't changed
      if (trimmedName === localEvent.name) {
        setIsEditingName(false);
        return;
      }

      // Make API call to update event name and section
      const response = await fetch(`https://${_host}:6003/api/${_company}/events/${event.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: trimmedName,
          section: generatedSection
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update event name');
      }

      // Update local state
      setLocalEvent({
        ...localEvent,
        name: trimmedName,
        section: generatedSection
      });

      setUpdatedSection(generatedSection);
      
      // Update the display if competition is active
      if (localEvent.active) {
        // Create display data from current event state
        const currentDisplayData = {
          eventId: event.id,
          eventName: trimmedName,
          roundType: localEvent.roundType
        };
        
        // If we have heat information, include it
        if (heats.length > 0 && localEvent.currentDanceIndex < heats.length) {
          const currentDance = heats[localEvent.currentDanceIndex];
          if (currentDance && currentDance.heats && 
              localEvent.currentHeatIndex < currentDance.heats.length) {
            const currentHeat = currentDance.heats[localEvent.currentHeatIndex];
            
            Object.assign(currentDisplayData, {
              danceName: currentDance.dance,
              heatNumber: currentHeat.heatNumber,
              dancers: currentHeat.dancers.map(d => d.clothNumber)
                .sort((a, b) => parseInt(a) - parseInt(b)),
              totalHeats: currentDance.heats.length,
              totalDances: heats.length,
              currentDanceIndex: localEvent.currentDanceIndex,
              currentHeatIndex: localEvent.currentHeatIndex
            });
          }
        }
        
        await updateDisplayData(currentDisplayData);
      }

      // Log event update
      if (currentUser) {
        auditLogger.logUpdateEvent(
          currentUser.username || currentUser.id,
          trimmedName,
          event.id
        );
      }

      setSuccess('Event name updated successfully');
      setTimeout(() => setSuccess(null), 3000);
      setIsEditingName(false);
    } catch (err) {
      console.error('Error updating event name:', err);
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleStopCompetition = async () => {
    try {
      setLoading(true);
      
      // Show confirmation dialog
      const confirmStop = window.confirm(
        "Are you sure you want to stop the competition? This will end the competition and return to the dancers tab."
      );
      
      if (!confirmStop) {
        setLoading(false);
        return;
      }
      
      // Update event status in API
      const response = await fetch(`https://${_host}:6003/api/${_company}/events/${event.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          active: false,
          currentDanceIndex: 0,
          currentHeatIndex: 0
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to stop competition');
      }
      
      // Update local state
      setLocalEvent({
        ...localEvent,
        active: false,
        currentDanceIndex: 0,
        currentHeatIndex: 0
      });
      
      // Update the display to show waiting for competition
      await updateDisplayData({
        eventId: event.id,
        eventName: event.name,
        roundType: event.roundType,
        waiting: true
      });
      
      setSuccess('Competition stopped successfully');
      setTimeout(() => setSuccess(null), 3000);
      
      // Switch to the dancers tab
      setActiveTab('dancers');
    } catch (err) {
      console.error('Error stopping competition:', err);
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Function to remove a dancer from the event
  const handleRemoveDancer = async (clothNumber) => {
    try {
      setLoading(true);

      // Make API call to remove dancer
      const response = await fetch(`https://${_host}:6003/api/${_company}/events/${event.id}/dancers/${clothNumber}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove dancer');
      }

      // Update local state by filtering out the removed dancer
      const updatedDancers = localEvent.dancers.filter(d => d.clothNumber !== clothNumber);
      const updatedEvent = { ...localEvent, dancers: updatedDancers };
      setLocalEvent(updatedEvent);

      // Show success message
      setSuccess(`Dancer with number ${clothNumber} removed successfully`);
      setTimeout(() => setSuccess(null), 3000);

      // Regenerate heats after removing dancer
      generateHeats(updatedEvent);
    } catch (err) {
      console.error('Error removing dancer:', err);
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Generate heat distributions for all dances - Updated to accept event parameter
  const generateHeats = (eventToProcess = localEvent) => {
    const { dancers, dances, numHeats } = eventToProcess;

  
    // Clear heats if no dancers, no dances, or invalid configuration
    if (!dancers || dancers.length === 0 ||
        !dances || dances.length === 0 ||
        !numHeats || numHeats === 0) {
        setHeats([]);
      return;
    }

    // Advanced heat distribution algorithm
    const generatedHeats = [];
    
    // Sort dancers by cloth number for consistent starting arrangement
    const sortedDancers = [...dancers].sort((a, b) => 
      parseInt(a.clothNumber) - parseInt(b.clothNumber)
    );
    
        
    // Generate heats for each dance with different distributions to ensure rotation
    dances.forEach((dance, danceIndex) => {
            
      const danceHeats = [];
      
      // Create a shifted version of the sorted dancers array for each dance
      const shiftAmount = danceIndex * Math.ceil(dancers.length / 3);
      let dancersForThisDance = [...sortedDancers];
      
      // Apply different rotation patterns for each dance
      if (danceIndex > 0) {
        switch (danceIndex % 5) {
          case 1: // Shift forward
            dancersForThisDance = [
              ...sortedDancers.slice(shiftAmount % sortedDancers.length),
              ...sortedDancers.slice(0, shiftAmount % sortedDancers.length)
            ];
            break;
          case 2: // Reverse the order
            dancersForThisDance = [
              ...sortedDancers.slice(sortedDancers.length - shiftAmount % sortedDancers.length),
              ...sortedDancers.slice(0, sortedDancers.length - shiftAmount % sortedDancers.length)
            ];
            break;
          case 3: // Alternate odd/even
            dancersForThisDance = [...sortedDancers].sort((a, b) => {
              return (parseInt(a.clothNumber) % 2) - (parseInt(b.clothNumber) % 2) || 
                     parseInt(a.clothNumber) - parseInt(b.clothNumber);
            });
            break;
          case 4: // Interlace from both ends
            const even = sortedDancers.filter((_, i) => i % 2 === 0);
            const odd = sortedDancers.filter((_, i) => i % 2 === 1);
            dancersForThisDance = [...even, ...odd];
            break;
          default:
            // Keep original order
            break;
        }
      }
      
            
      // Calculate dancers per heat (distribute as evenly as possible)
      const dancersPerHeat = Array(numHeats).fill(0)
        .map((_, i) => Math.floor(dancers.length / numHeats) + (i < dancers.length % numHeats ? 1 : 0));
      
            
      // Create heats for this dance
      let dancerIndex = 0;
      for (let heatIndex = 0; heatIndex < numHeats; heatIndex++) {
        const heatDancers = [];
        
        // Add dancers to this heat
        for (let i = 0; i < dancersPerHeat[heatIndex]; i++) {
          if (dancerIndex < dancersForThisDance.length) {
            heatDancers.push(dancersForThisDance[dancerIndex]);
            dancerIndex++;
          }
        }
        
                
        danceHeats.push({
          heatNumber: heatIndex + 1,
          dancers: heatDancers
        });
      }
      
      generatedHeats.push({
        dance,
        heats: danceHeats
      });
    });

    
    // Log heat generation
    if (currentUser && generatedHeats.length > 0) {
      const totalHeats = generatedHeats.reduce((sum, dance) => sum + dance.heats.length, 0);
      auditLogger.logHeatGeneration(
        currentUser.username || currentUser.id,
        eventToProcess.id,
        eventToProcess.name,
        totalHeats
      );
    }

    setHeats(generatedHeats);
  };

  // Function to start the competition
  const handleStartCompetition = async () => {
    try {
      setLoading(true);

      if (localEvent.dancers.length === 0) {
        throw new Error('Cannot start competition with no dancers');
      }

      if (heats.length === 0) {
        throw new Error('No heats generated. Please add dancers and generate heats first.');
      }

      // Check if there's any currently active competition and stop it first
      const eventsResponse = await fetch(`https://${_host}:6003/api/${_company}/events`);
      if (eventsResponse.ok) {
        const allEvents = await eventsResponse.json();
        const activeEvent = allEvents.find(e => e.active && e.id !== event.id);

        if (activeEvent) {
          
          // Stop the currently active competition
          const stopResponse = await fetch(`https://${_host}:6003/api/${_company}/events/${activeEvent.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              active: false
            })
          });

          if (!stopResponse.ok) {
            throw new Error(`Failed to stop currently active competition: ${activeEvent.name}`);
          }

          
          // Reset the display to idle state when stopping previous competition
          const displayResponse = await fetch(`https://${_host}:6003/api/${_company}/display`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              waiting: true,
              active: false
            })
          });

          if (!displayResponse.ok) {
            console.warn('Warning: Failed to reset display to idle state');
          }
        }
      }

      // Check if all dancers are checked in at Registration
      const uncheckedDancers = localEvent.dancers.filter(dancer => !dancer.registrationCheckin);

      if (uncheckedDancers.length > 0) {
        const dancerNames = uncheckedDancers.map(dancer => {
          const leadName = dancer.leadName || '';
          const followName = dancer.followName || '';

          if (leadName && followName) {
            return `${leadName} & ${followName}`;
          } else if (leadName) {
            return leadName;
          } else if (followName) {
            return followName;
          }
          return `Cloth #${dancer.clothNumber}`;
        }).join(', ');

        throw new Error(`Cannot start competition. The following dancer(s) have not checked in at Registration: ${dancerNames}. All dancers must be checked in before the competition can start.`);
      }
      
      // Set the event as active and reset to the first dance and heat
      const updatedEvent = { 
        ...localEvent, 
        active: true,
        currentDanceIndex: 0,
        currentHeatIndex: 0
      };
      
      // Make API call to update event
      const response = await fetch(`https://${_host}:6003/api/${_company}/events/${event.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          active: true,
          currentDanceIndex: 0,
          currentHeatIndex: 0
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start competition');
      }
      
      // Update local state
      setLocalEvent(updatedEvent);
      
      // Update the display with the first heat
      const currentDance = heats[0];
      const currentHeat = currentDance.heats[0];
      
      await updateDisplayData({
        eventId: event.id,
        eventName: event.name,
        roundType: event.roundType,
        danceName: currentDance.dance,
        heatNumber: currentHeat.heatNumber,
        dancers: currentHeat.dancers.map(d => d.clothNumber),
        totalHeats: currentDance.heats.length,
        totalDances: heats.length,
        currentDanceIndex: 0,
        currentHeatIndex: 0,
        waiting: false  // Explicitly set waiting to false when starting competition
      });
      
      // Switch to the control tab
      setActiveTab('control');
    } catch (err) {
      console.error('Error starting competition:', err);
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBackground = async (background) => {
    try {
      // Get current display data
      const response = await fetch('https://'+_host+':6003/api/'+_company+'/display');
      
      if (!response.ok) {
        throw new Error('Failed to fetch current display data');
      }
      
      const currentDisplayData = await response.json();
      
      // Update display data with new background
      const updatedDisplayData = {
        ...currentDisplayData,
        backgroundId: background ? background.id : null
      };
      
      // Update display
      await updateDisplayData(updatedDisplayData);
    } catch (err) {
      console.error('Error updating background:', err);
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    }
  };

  // Function to update the display data
  const updateDisplayData = async (displayData) => {
    try {
      // If the displayData has a backgroundId, keep it when updating the display
      if (!displayData.backgroundId && displayData.preserveBackground !== false) {
        // Fetch current display data to get background info
        const currentDisplayResponse = await fetch('https://'+_host+':6003/api/'+_company+'/display');
        if (currentDisplayResponse.ok) {
          const currentDisplay = await currentDisplayResponse.json();
          if (currentDisplay && currentDisplay.backgroundId) {
            displayData.backgroundId = currentDisplay.backgroundId;
          }
        }
      }
      
      // Make API call to update display - include both camelCase and snake_case for backend compatibility
      const enhancedDisplayData = {
        ...displayData,
        // Include snake_case variants for backend compatibility
        event_id: displayData.eventId,
        event_name: displayData.eventName,
        round_type: displayData.roundType,
        dance_name: displayData.danceName,
        heat_number: displayData.heatNumber,
        total_heats: displayData.totalHeats,
        total_dances: displayData.totalDances,
        current_dance_index: displayData.currentDanceIndex,
        current_heat_index: displayData.currentHeatIndex,
        // Include boolean fields in snake_case for compatibility
        waiting: displayData.waiting
      };

      
      const response = await fetch('https://'+_host+':6003/api/'+_company+'/display', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(enhancedDisplayData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update display');
      }
      
      // Call parent component's onUpdateDisplay if provided
      if (onUpdateDisplay) {
        onUpdateDisplay(displayData);
      }
      
      return await response.json();
    } catch (err) {
      console.error('Error updating display data:', err);
      throw err;
    }
  };

  // Function to move to the next heat/dance
  const handleNextHeat = async () => {
    try {
      setLoading(true);

      // Safety checks
      if (!heats || heats.length === 0) {
        throw new Error('No heats available. Please generate heats first.');
      }

      const { currentDanceIndex = 0, currentHeatIndex = 0 } = localEvent;
      const currentDance = heats[currentDanceIndex];

      if (!currentDance) {
        throw new Error(`Invalid dance index: ${currentDanceIndex}. Total dances: ${heats.length}`);
      }

      if (!currentDance.heats || currentDance.heats.length === 0) {
        throw new Error('No heats found for current dance');
      }

      const totalHeatsInDance = currentDance.heats.length;
      
      let nextDanceIndex = currentDanceIndex;
      let nextHeatIndex = currentHeatIndex + 1;
      
      // Check if we need to move to the next dance
      if (nextHeatIndex >= totalHeatsInDance) {
        nextHeatIndex = 0;
        nextDanceIndex = currentDanceIndex + 1;
        
        // Check if we've completed all dances
        if (nextDanceIndex >= heats.length) {
          // Competition is complete
          // Update event status in API
          const response = await fetch(`https://${_host}:6003/api/${_company}/events/${event.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              active: false,
              currentDanceIndex: 0,
              currentHeatIndex: 0
            })
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to complete competition');
          }
          
          // Update local state
          setLocalEvent({
            ...localEvent,
            active: false,
            currentDanceIndex: 0,
            currentHeatIndex: 0
          });
          
          // Update the display to show completion
          await updateDisplayData({
            eventId: event.id,
            eventName: event.name,
            roundType: event.roundType,
            waiting: true  // Show logo when competition is completed
          });
          
          setSuccess('Competition completed!');
          setTimeout(() => setSuccess(null), 3000);
          return;
        }
      }
      
      // Update the event state in API
      const response = await fetch(`https://${_host}:6003/api/${_company}/events/${event.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentDanceIndex: nextDanceIndex,
          currentHeatIndex: nextHeatIndex
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to move to next heat');
      }
      
      // Update local state
      setLocalEvent({
        ...localEvent,
        currentDanceIndex: nextDanceIndex,
        currentHeatIndex: nextHeatIndex
      });
      
      // Update the display with safety checks
      const nextDance = heats[nextDanceIndex];
      if (!nextDance || !nextDance.heats) {
        throw new Error(`Invalid dance data for index: ${nextDanceIndex}`);
      }
      if (nextHeatIndex < 0 || nextHeatIndex >= nextDance.heats.length) {
        throw new Error(`Invalid heat index: ${nextHeatIndex}. Total heats: ${nextDance.heats.length}`);
      }
      const nextHeat = nextDance.heats[nextHeatIndex];
      
      await updateDisplayData({
        eventId: event.id,
        eventName: event.name,
        roundType: event.roundType,
        danceName: nextDance.dance,
        heatNumber: nextHeat.heatNumber,
        dancers: nextHeat.dancers.map(d => d.clothNumber),
        totalHeats: nextDance.heats.length,
        totalDances: heats.length,
        currentDanceIndex: nextDanceIndex,
        currentHeatIndex: nextHeatIndex
      });
    } catch (err) {
      console.error('Error moving to next heat:', err);
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Function to move to the previous heat/dance
  const handlePreviousHeat = async () => {
    try {
      setLoading(true);

      // Safety checks
      if (!heats || heats.length === 0) {
        throw new Error('No heats available. Please generate heats first.');
      }

      const { currentDanceIndex = 0, currentHeatIndex = 0 } = localEvent;

      let prevDanceIndex = currentDanceIndex;
      let prevHeatIndex = currentHeatIndex - 1;

      // Check if we need to move to the previous dance
      if (prevHeatIndex < 0) {
        prevDanceIndex = currentDanceIndex - 1;

        // Check if we've gone before the first dance
        if (prevDanceIndex < 0) {
          // We're already at the first heat of the first dance
          setLoading(false);
          return;
        }

        // Safety check for the previous dance
        if (!heats[prevDanceIndex] || !heats[prevDanceIndex].heats) {
          throw new Error(`Invalid previous dance index: ${prevDanceIndex}. Total dances: ${heats.length}`);
        }

        // Move to the last heat of the previous dance
        prevHeatIndex = heats[prevDanceIndex].heats.length - 1;
      }
      
      // Update the event state in API
      const response = await fetch(`https://${_host}:6003/api/${_company}/events/${event.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentDanceIndex: prevDanceIndex,
          currentHeatIndex: prevHeatIndex
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to move to previous heat');
      }
      
      // Update local state
      setLocalEvent({
        ...localEvent,
        currentDanceIndex: prevDanceIndex,
        currentHeatIndex: prevHeatIndex
      });
      
      // Update the display with safety checks
      const prevDance = heats[prevDanceIndex];
      if (!prevDance || !prevDance.heats) {
        throw new Error(`Invalid dance data for index: ${prevDanceIndex}`);
      }
      if (prevHeatIndex < 0 || prevHeatIndex >= prevDance.heats.length) {
        throw new Error(`Invalid heat index: ${prevHeatIndex}. Total heats: ${prevDance.heats.length}`);
      }
      const prevHeat = prevDance.heats[prevHeatIndex];
      
      await updateDisplayData({
        eventId: event.id,
        eventName: event.name,
        roundType: event.roundType,
        danceName: prevDance.dance,
        heatNumber: prevHeat.heatNumber,
        dancers: prevHeat.dancers.map(d => d.clothNumber),
        totalHeats: prevDance.heats.length,
        totalDances: heats.length,
        currentDanceIndex: prevDanceIndex,
        currentHeatIndex: prevHeatIndex
      });
    } catch (err) {
      console.error('Error moving to previous heat:', err);
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="event-detail">
      <div className="event-detail-header">
        {isEditingName ? (
          <div className="event-name-edit">
            <input
              type="text"
              value={updatedEventName}
              onChange={(e) => setUpdatedEventName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleUpdateEventName();
                }
              }}
              autoFocus
              className="event-name-input"
            />
            <div className="event-name-edit-buttons">
              <button 
                className="save-name-btn"
                onClick={handleUpdateEventName}
                disabled={loading}
              >
                Save
              </button>
              <button 
                className="cancel-name-btn"
                onClick={() => {
                  setIsEditingName(false);
                  setUpdatedEventName(localEvent.name);
                }}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <h2 className="event-name">
              {localEvent.name}
              {!localEvent.active && (
                <button
                  className="edit-name-btn"
                  onClick={() => setIsEditingName(true)}
                  title="Edit event name"
                >
                  ✎
                </button>
              )}
            </h2>
            {localEvent.section && (
              <div className="event-section">
                {localEvent.section}
              </div>
            )}
          </>
        )}
        <div className="event-detail-meta">
          <span className="event-detail-round">{localEvent.roundType}</span>
          <span className="event-detail-dances">
            {localEvent.dances && Array.isArray(localEvent.dances)
              ? localEvent.dances.join(', ')
              : 'No dances defined'
            }
          </span>
          {localEvent.roundType !== 'Final' && (
            <span className="event-detail-heats">{localEvent.numHeats || 0} Heats</span>
          )}
        </div>
      </div>
      
      <div className="event-detail-tabs">
        <button
          className={`tab ${activeTab === 'dancers' ? 'active' : ''}`}
          onClick={() => setActiveTab('dancers')}
        >
          Dancers ({(localEvent.dancers && Array.isArray(localEvent.dancers) ? localEvent.dancers.length : 0)})
        </button>
        <button
          className={`tab ${activeTab === 'heats' ? 'active' : ''}`}
          onClick={() => setActiveTab('heats')}
        >
          Heats
        </button>
        {localEvent.active && (
          <button
            className={`tab ${activeTab === 'control' ? 'active' : ''}`}
            onClick={() => setActiveTab('control')}
          >
            Control Panel
          </button>
        )}
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <div className="event-detail-content">
        {activeTab === 'dancers' && (
          <div className="dancers-tab">
            <div className="dancers-list">
              <h3>Registered Dancers</h3>
              {localEvent.dancers.length === 0 ? (
                <p className="no-dancers-message">No dancers registered for this event yet.</p>
              ) : (
                <table className="dancers-table">
                  <thead>
                    <tr>
                      <th>Cloth Number</th>
                      <th>Name</th>
                      <th>Registration Desk</th>
                      <th>Marshall Area</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...localEvent.dancers]
                      .sort((a, b) => parseInt(a.clothNumber) - parseInt(b.clothNumber))
                      .map((dancer) => (
                        <tr key={dancer.clothNumber}>
                          <td>{dancer.clothNumber}</td>
                          <td>{dancer.name}</td>
                          <td>
                            <span className={`checkin-status-badge ${dancer.registrationCheckin ? 'checked-in' : 'not-checked-in'}`}>
                              {dancer.registrationCheckin ? '✓ Checked In' : '✗ Not Checked In'}
                            </span>
                          </td>
                          <td>
                            <span className={`checkin-status-badge ${dancer.marshallCheckin ? 'checked-in' : 'not-checked-in'}`}>
                              {dancer.marshallCheckin ? '✓ Checked In' : '✗ Not Checked In'}
                            </span>
                          </td>
                          <td>
                            <button
                              className="remove-dancer-btn"
                              onClick={() => handleRemoveDancer(dancer.clothNumber)}
                              disabled={loading || localEvent.active}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {localEvent.dancers.length > 0 && !localEvent.active && (
              <button
                className="start-competition-btn"
                onClick={handleStartCompetition}
                disabled={loading}
              >
                Start Competition
              </button>
            )}
          </div>
        )}

        {activeTab === 'heats' && (
          <div className="heats-tab">
            {loading ? (
              <div className="loading-message">Generating heats...</div>
            ) : (
              <HeatDistribution
                event={localEvent}
                heats={heats}
                onRegenerateHeats={() => generateHeats(localEvent)}
              />
            )}
          </div>
        )}
        
        {activeTab === 'control' && localEvent.active && (
          <div className="control-tab">
            <CompetitionControl
              event={localEvent}
              heats={heats}
              onNextHeat={handleNextHeat}
              onPreviousHeat={handlePreviousHeat}
              onStopCompetition={handleStopCompetition}
              onUpdateBackground={handleUpdateBackground}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetail;